import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  ProgressBar,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-react";
import {
  Stage,
  UatProject,
  UatProjectOrder,
  UatProjectSync,
} from "../../lusca-search-api/generated-src";
import useAsanaAuthorization, {
  isAsanaAuthorizationError,
} from "../../hooks/useAsanaAuthorization";
import useUatProjects from "../../hooks/useUatProjects";
import {
  DetailsApiFactory,
  RedriveApiFactory,
  ShadowTestApiFactory,
} from "../../lusca-search-api/LuscaSearchApiFactory";
import useShadowTestResult from "../../hooks/useShadowTestResult";
import useClassifications from "../../hooks/useClassifications";
import ShadowTestResultModal from "../summary/ShadowTestResultModal";
import { getShadowTestResultStatus } from "../../helpers/shadowTestResultStatus";
import type { ShadowTestResultResponseWithCompat } from "../../helpers/shadowTestResultStatus";
import { ComparisonEnvironment } from "../../lusca-search-api/generated-src";
import usePermissions from "../../hooks/usePermissions";
import useFetchDetails from "../../hooks/useFetchDetails";
import useFetchDlqInfo from "../../hooks/useFetchDlqInfo";
import HealthStatusIndicator from "../common/HealthStatusIndicator";
import {
  HealthStateProvider,
  initialHealthState,
  useHealthStateUpdate,
} from "../../contexes/HealthStateContext";
import { calculateCompleteHealthState } from "../../helpers/healthUtils";
import { isFreeTrial } from "../../helpers/freeTrialUtils";
import { useHealthStatus } from "../../hooks/useHealthStatus";
import {
  getCountryCodeFromMarketplace,
  resolveShadowTestCountryCode,
} from "../../helpers/marketplaceUtils";
import {
  deriveOrderDetails,
  SummaryEvent,
} from "../../helpers/summaryDataUtils";
import { AccountingEventsGroup } from "../../types/events";

function asanaProjectGidFromUrl(value: string): string | undefined {
  const match = value.match(/(?:\/0\/|\/project\/)(\d+)(?:\/|$)/);
  return match?.[1];
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseBody: unknown = error.response?.data;
    if (
      typeof responseBody === "object" &&
      responseBody !== null &&
      "error" in responseBody &&
      typeof (responseBody as { error?: unknown }).error === "string"
    ) {
      return (responseBody as { error: string }).error;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "The UAT Project request could not be completed.";
}

async function resolveShadowTestTransactionId(
  stage: Stage,
  transactionId: string
): Promise<string> {
  const response = await DetailsApiFactory().getDetails(stage, transactionId);
  return response.data.fallback?.resolvedId || transactionId;
}

function isRunning(sync?: UatProjectSync): boolean {
  return sync?.status === "pending" || sync?.status === "running";
}

function statusType(
  project: UatProject,
  sync?: UatProjectSync
): "error" | "loading" | "pending" | "success" {
  if (isRunning(sync)) {
    return "loading";
  }
  if (project.lastSync?.status === "completed") {
    return "success";
  }
  if (project.lastSync?.status === "failed") {
    return "error";
  }
  return "pending";
}

function statusText(project: UatProject, sync?: UatProjectSync): string {
  if (isRunning(sync)) {
    const progress = sync?.progress;
    if (progress && progress.total > 0) {
      return `Syncing ${progress.completed} of ${progress.total} tasks`;
    }
    return sync?.status === "pending"
      ? "Starting Asana sync"
      : "Syncing from Asana";
  }
  if (project.lastSync?.status === "completed") {
    const { importedCount = 0, skippedCount } = project.lastSync;
    return skippedCount === undefined
      ? `Asana sync completed: ${importedCount} orders imported`
      : `Asana sync completed: ${importedCount} orders imported; ${skippedCount} tasks skipped`;
  }
  if (project.lastSync?.status === "failed") {
    return "Asana sync failed";
  }
  return "Awaiting Asana sync";
}

function syncActionText(project: UatProject, sync?: UatProjectSync): string {
  if (isRunning(sync)) {
    return "Syncing from Asana";
  }
  if (project.lastSync?.status === "failed") {
    return "Retry Asana sync";
  }
  if (project.lastSync?.status === "completed") {
    return "Sync again";
  }
  return "Sync from Asana";
}

function ProjectOrderHealthContent({
  stage,
  transactionId,
}: {
  stage: Stage;
  transactionId: string;
}) {
  const details = useFetchDetails(stage, transactionId);
  const dlqInfo = useFetchDlqInfo(stage, transactionId);
  const { setState } = useHealthStateUpdate();
  const eventData = (details.data?.events ?? []) as SummaryEvent[];
  const cbeData = details.data?.cbes ?? [];
  const healthDlqInfo = useMemo(
    () => ({
      status: dlqInfo.status,
      dlqItems: dlqInfo.dlqItems,
      error: dlqInfo.error,
    }),
    [dlqInfo.dlqItems, dlqInfo.error, dlqInfo.status]
  );
  const healthState = useMemo(
    () =>
      details.data
        ? calculateCompleteHealthState(
            eventData,
            cbeData,
            healthDlqInfo,
            undefined,
            isFreeTrial(eventData)
          )
        : initialHealthState,
    [cbeData, details.data, eventData, healthDlqInfo]
  );
  const { flowSteps } = useHealthStatus(dlqInfo.status);

  useEffect(() => {
    details.refetch().catch(() => undefined);
  }, [details.refetch]);

  useEffect(() => {
    setState(healthState);
  }, [healthState, setState]);

  return (
    <HealthStatusIndicator
      healthStatus={healthState.overallHealth}
      flowSteps={flowSteps}
      isLoading={
        !details.data || details.isFetching || dlqInfo.status === "loading"
      }
      eventData={eventData}
      cbeData={cbeData}
    />
  );
}

function ProjectOrderHealthCell({
  stage,
  transactionId,
}: {
  stage: Stage;
  transactionId: string;
}) {
  return (
    <HealthStateProvider>
      <ProjectOrderHealthContent stage={stage} transactionId={transactionId} />
    </HealthStateProvider>
  );
}

function asanaStatus(): React.ReactNode {
  return <StatusIndicator type="success">Open</StatusIndicator>;
}

interface ShadowTestStatusCellProps {
  project: UatProject;
  order: UatProjectOrder;
  awaitResult: boolean;
}

function ShadowTestStatusCell({
  project,
  order,
  awaitResult,
}: ShadowTestStatusCellProps) {
  const wasAwaitingTransactionId = useRef<string>();
  const [shadowTestTransactionId, setShadowTestTransactionId] = useState(
    order.transactionId
  );
  const [isResolvingTransactionId, setIsResolvingTransactionId] =
    useState(true);

  useEffect(() => {
    let active = true;
    setShadowTestTransactionId(order.transactionId);
    setIsResolvingTransactionId(true);
    resolveShadowTestTransactionId(project.stage, order.transactionId)
      .then((resolvedId) => {
        if (active) {
          setShadowTestTransactionId(resolvedId);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setIsResolvingTransactionId(false);
        }
      });
    return () => {
      active = false;
    };
  }, [order.transactionId, project.stage]);

  const {
    gammaResult,
    gammaIsLoading,
    gammaError,
    awaitNewResult,
    awaitingEnvironment,
  } = useShadowTestResult({
    stage: project.stage,
    transactionId: shadowTestTransactionId,
  });

  useEffect(() => {
    if (
      awaitResult &&
      wasAwaitingTransactionId.current !== shadowTestTransactionId
    ) {
      awaitNewResult(ComparisonEnvironment.Gamma);
    }
    wasAwaitingTransactionId.current = awaitResult
      ? shadowTestTransactionId
      : undefined;
  }, [awaitNewResult, awaitResult, shadowTestTransactionId]);

  if (isResolvingTransactionId) {
    return <StatusIndicator type="loading">Checking</StatusIndicator>;
  }

  if (awaitingEnvironment === ComparisonEnvironment.Gamma) {
    return <StatusIndicator type="loading">Testing</StatusIndicator>;
  }
  if (gammaIsLoading) {
    return <StatusIndicator type="loading">Checking</StatusIndicator>;
  }
  if (gammaError) {
    return <StatusIndicator type="error">Unavailable</StatusIndicator>;
  }
  if (!gammaResult) {
    return <StatusIndicator type="pending">Not run</StatusIndicator>;
  }

  const status = getShadowTestResultStatus(gammaResult);
  return (
    <StatusIndicator
      type={
        status === "PASSED"
          ? "success"
          : status === "FAILED"
          ? "error"
          : "warning"
      }
    >
      {status === "PASSED"
        ? "Passed"
        : status === "FAILED"
        ? "Failed"
        : "Blocked"}
    </StatusIndicator>
  );
}

interface ProjectOrdersTableProps {
  project: UatProject;
  orders: UatProjectOrder[];
}

type BulkAction = "redrive" | "test";

function ProjectOrdersTable({ project, orders }: ProjectOrdersTableProps) {
  const { canRedrive } = usePermissions();
  const [redrivingOrderId, setRedrivingOrderId] = useState<string>();
  const [redriveMessage, setRedriveMessage] = useState<string>();
  const [redriveError, setRedriveError] = useState<string>();
  const [pendingBulkAction, setPendingBulkAction] = useState<BulkAction>();
  const [activeBulkAction, setActiveBulkAction] = useState<BulkAction>();
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const [pendingShadowOrderIds, setPendingShadowOrderIds] = useState<
    Set<string>
  >(() => new Set());
  const [selectedShadowOrder, setSelectedShadowOrder] =
    useState<UatProjectOrder>();
  const [selectedShadowTransactionId, setSelectedShadowTransactionId] =
    useState<string>();
  const [
    isResolvingSelectedShadowTransaction,
    setIsResolvingSelectedShadowTransaction,
  ] = useState(false);
  const [comparisonEnvironment, setComparisonEnvironment] =
    useState<ComparisonEnvironment>(ComparisonEnvironment.Gamma);
  const classifications = useClassifications(project.stage);
  const {
    gammaResult,
    betaResult,
    gammaIsLoading,
    betaIsLoading,
    gammaIsFetching,
    betaIsFetching,
    gammaError,
    betaError,
    refetch,
  } = useShadowTestResult({
    stage: project.stage,
    transactionId: selectedShadowTransactionId,
  });
  const shadowTestResult =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaResult
      : gammaResult;
  const isShadowTestLoading =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsLoading
      : gammaIsLoading;
  const isShadowTestFetching =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsFetching
      : gammaIsFetching;
  const shadowTestError =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaError
      : gammaError;
  const availableComparisonEnvironments = [
    ...(gammaResult ? [ComparisonEnvironment.Gamma] : []),
    ...(betaResult ? [ComparisonEnvironment.Beta] : []),
  ];

  const redrive = async (order: UatProjectOrder) => {
    setRedriveMessage(undefined);
    setRedriveError(undefined);
    setRedrivingOrderId(order.transactionId);
    try {
      const response = await RedriveApiFactory().postRedriveOrder(
        project.stage,
        { orderId: order.transactionId }
      );
      setRedriveMessage(
        `Redrive requested for ${order.transactionId}${
          response.data.executionId ? ` (${response.data.executionId})` : ""
        }.`
      );
    } catch (error) {
      setRedriveError(errorMessage(error));
    } finally {
      setRedrivingOrderId(undefined);
    }
  };

  const startShadowTest = async (order: UatProjectOrder) => {
    const details = await DetailsApiFactory().getDetails(
      project.stage,
      order.transactionId
    );
    const eventData = (details.data.events ?? []) as unknown as SummaryEvent[];
    const shadowTestTransactionId =
      details.data.fallback?.resolvedId || order.transactionId;
    const orderDetails = deriveOrderDetails(eventData);
    const accountingEventsGroup = eventData.find(
      (item): item is AccountingEventsGroup => "accountingEvents" in item
    );
    const firstEvent = accountingEventsGroup
      ? Object.values(accountingEventsGroup.accountingEvents)[0]
      : undefined;
    const marketplace = firstEvent?.marketplaceId
      ? getCountryCodeFromMarketplace(firstEvent.marketplaceId).replace(
          "_PROD",
          ""
        )
      : "";
    const countryCode = resolveShadowTestCountryCode(
      marketplace,
      orderDetails.declaredCountryCode,
      firstEvent?.declaredCountryCode
    );

    if (!marketplace || !orderDetails.glProductGroup || !countryCode) {
      throw new Error(
        "Could not derive the shadow-test inputs from this order."
      );
    }

    await ShadowTestApiFactory().createShadowTest(project.stage, {
      marketplace,
      glProductGroupId: String(orderDetails.glProductGroup),
      transactionIds: [shadowTestTransactionId],
      countryCode,
      auid: firstEvent?.accountingUniqueId || undefined,
      subscriptionId: firstEvent?.subscriptionId || undefined,
      periodNumber: firstEvent?.subscriptionPeriod || undefined,
      deliveryPacketItemId: firstEvent?.deliveryPacketItemId
        ? String(firstEvent.deliveryPacketItemId)
        : undefined,
      digitalOrderID: firstEvent?.orderId || undefined,
      financeTagAuthReferenceId:
        firstEvent?.financeTagAuthReferenceId || undefined,
    });
  };

  const openShadowTestResults = (order: UatProjectOrder) => {
    setComparisonEnvironment(ComparisonEnvironment.Gamma);
    setSelectedShadowOrder(order);
    setSelectedShadowTransactionId(undefined);
    setIsResolvingSelectedShadowTransaction(true);
    resolveShadowTestTransactionId(project.stage, order.transactionId)
      .then(setSelectedShadowTransactionId)
      .catch(() => setSelectedShadowTransactionId(order.transactionId))
      .finally(() => setIsResolvingSelectedShadowTransaction(false));
  };

  const runBulkAction = async () => {
    if (!pendingBulkAction) {
      return;
    }
    const action = pendingBulkAction;
    setPendingBulkAction(undefined);
    setRedriveMessage(undefined);
    setRedriveError(undefined);
    setIsBulkRunning(true);
    setActiveBulkAction(action);
    const failures: string[] = [];
    let succeeded = 0;

    for (const order of orders) {
      try {
        if (action === "redrive") {
          await RedriveApiFactory().postRedriveOrder(project.stage, {
            orderId: order.transactionId,
          });
        } else {
          await startShadowTest(order);
          setPendingShadowOrderIds((current) =>
            new Set(current).add(order.transactionId)
          );
        }
        succeeded += 1;
      } catch (error) {
        failures.push(`${order.transactionId}: ${errorMessage(error)}`);
      }
    }

    const verb =
      action === "redrive" ? "Redrive requested" : "Shadow tests started";
    setRedriveMessage(`${verb} for ${succeeded} of ${orders.length} orders.`);
    if (failures.length > 0) {
      setRedriveError(failures.join(" "));
    }
    setIsBulkRunning(false);
    setActiveBulkAction(undefined);
  };

  const actionLabel =
    pendingBulkAction === "redrive" ? "Redrive all" : "Test all";

  return (
    <SpaceBetween size="xs">
      {redriveMessage && <Alert type="success">{redriveMessage}</Alert>}
      {redriveError && <Alert type="error">{redriveError}</Alert>}
      <Table
        items={orders}
        variant="embedded"
        columnDefinitions={[
          {
            id: "order",
            header: "Transaction ID",
            cell: (order) => (
              <Link
                href={`/find?transactionId=${encodeURIComponent(
                  order.transactionId
                )}`}
              >
                {order.transactionId}
              </Link>
            ),
          },
          {
            id: "test-case",
            header: "Test case",
            cell: (order) => (
              <Link href={order.taskUrl} external>
                {order.taskName}
              </Link>
            ),
          },
          {
            id: "shadow-test",
            header: "Shadow test",
            cell: (order) => (
              <ShadowTestStatusCell
                project={project}
                order={order}
                awaitResult={pendingShadowOrderIds.has(order.transactionId)}
              />
            ),
          },
          {
            id: "health",
            header: "Health",
            cell: (order) => (
              <ProjectOrderHealthCell
                stage={project.stage}
                transactionId={order.transactionId}
              />
            ),
          },
          {
            id: "asana-status",
            header: "Asana",
            cell: asanaStatus,
          },
          {
            id: "actions",
            header: "Actions",
            cell: (order) => (
              <SpaceBetween direction="horizontal" size="xs">
                {canRedrive && (
                  <Button
                    loading={redrivingOrderId === order.transactionId}
                    disabled={Boolean(redrivingOrderId) || isBulkRunning}
                    onClick={() => redrive(order)}
                  >
                    Redrive
                  </Button>
                )}
                <Button
                  disabled={isBulkRunning}
                  onClick={() => openShadowTestResults(order)}
                >
                  Test results
                </Button>
              </SpaceBetween>
            ),
          },
        ]}
        empty={<Box textAlign="center">No imported orders yet.</Box>}
        header={
          <Header
            variant="h3"
            counter={`(${orders.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {canRedrive && (
                  <Button
                    loading={isBulkRunning && activeBulkAction === "redrive"}
                    disabled={isBulkRunning}
                    onClick={() => setPendingBulkAction("redrive")}
                  >
                    Redrive all
                  </Button>
                )}
                <Button
                  loading={isBulkRunning && activeBulkAction === "test"}
                  disabled={isBulkRunning}
                  onClick={() => setPendingBulkAction("test")}
                >
                  Test all
                </Button>
              </SpaceBetween>
            }
          >
            Imported orders
          </Header>
        }
      />
      <Modal
        visible={Boolean(pendingBulkAction)}
        onDismiss={() => !isBulkRunning && setPendingBulkAction(undefined)}
        header={`${actionLabel} (${orders.length})?`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPendingBulkAction(undefined)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={runBulkAction}>
                {actionLabel}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {pendingBulkAction === "redrive"
          ? `This starts a redrive for every imported order in ${project.projectName}.`
          : `This derives each order's Raven inputs and starts a shadow test for every imported order in ${project.projectName}.`}
      </Modal>
      <ShadowTestResultModal
        visible={Boolean(selectedShadowOrder)}
        onDismiss={() => {
          setSelectedShadowOrder(undefined);
          setSelectedShadowTransactionId(undefined);
          setIsResolvingSelectedShadowTransaction(false);
        }}
        result={
          shadowTestResult as
            | ShadowTestResultResponseWithCompat
            | null
            | undefined
        }
        isLoading={isResolvingSelectedShadowTransaction || isShadowTestLoading}
        isRefreshing={isShadowTestFetching}
        error={shadowTestError}
        onRefresh={() => {
          refetch().catch((refreshError) =>
            console.error("Failed to refresh shadow test result:", refreshError)
          );
        }}
        classifications={classifications}
        comparisonEnvironment={comparisonEnvironment}
        availableComparisonEnvironments={availableComparisonEnvironments}
        onComparisonEnvironmentChange={setComparisonEnvironment}
        onTriggerTest={() => {}}
        isTriggerLoading={false}
        triggerError={null}
        canTrigger={false}
      />
    </SpaceBetween>
  );
}

interface ActiveSync {
  projectId: string;
  sync: UatProjectSync;
}

interface ProjectListContentProps {
  projects: UatProject[];
  projectOrders: Record<string, UatProjectOrder[]>;
  activeSync?: ActiveSync;
  onSync: (project: UatProject) => void;
}

function ProjectListContent({
  projects,
  projectOrders,
  activeSync,
  onSync,
}: ProjectListContentProps) {
  if (projects.length === 0) {
    return (
      <Box textAlign="center" color="text-status-inactive" padding="l">
        No matching UAT Projects.
      </Box>
    );
  }

  return (
    <SpaceBetween size="m">
      {projects.map((project) => {
        const sync =
          activeSync?.projectId === project.id ? activeSync.sync : undefined;
        return (
          <Container
            key={project.id}
            header={
              <Header
                variant="h3"
                description={`${project.stage} · Updated ${new Date(
                  project.updatedAt
                ).toLocaleString()}`}
                actions={
                  <Button
                    loading={isRunning(sync)}
                    onClick={() => onSync(project)}
                  >
                    {syncActionText(project, sync)}
                  </Button>
                }
              >
                {project.projectName}
              </Header>
            }
          >
            <SpaceBetween size="s">
              {project.description && <Box>{project.description}</Box>}
              <SpaceBetween direction="horizontal" size="s">
                <StatusIndicator type={statusType(project, sync)}>
                  {statusText(project, sync)}
                </StatusIndicator>
                <Link href={project.asanaProjectUrl} external>
                  Asana project
                </Link>
                <Box color="text-body-secondary">
                  {project.orderCount ?? 0} imported orders
                </Box>
              </SpaceBetween>
              {isRunning(sync) && sync?.progress && (
                <ProgressBar
                  value={
                    sync.progress.total === 0
                      ? 0
                      : Math.round(
                          (sync.progress.completed / sync.progress.total) * 100
                        )
                  }
                  label="Syncing Asana tasks"
                  description={`${sync.progress.completed} of ${sync.progress.total} tasks validated`}
                />
              )}
              {project.lastSync?.error && (
                <Alert type="warning">{project.lastSync.error}</Alert>
              )}
              {project.orderCount !== undefined && project.orderCount > 0 && (
                <ProjectOrdersTable
                  project={project}
                  orders={projectOrders[project.id] ?? []}
                />
              )}
            </SpaceBetween>
          </Container>
        );
      })}
    </SpaceBetween>
  );
}

export default function ProjectList() {
  const {
    projects,
    projectOrders,
    isLoading,
    error,
    createProject,
    startSync,
    getSync,
    refresh,
    isCreating,
    createError,
    syncError,
  } = useUatProjects();
  const {
    isAuthorized,
    needsReconnect,
    isConnecting,
    authorizationError,
    beginAuthorization,
    markUnauthorized,
  } = useAsanaAuthorization();
  const [createVisible, setCreateVisible] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [stage, setStage] = useState<Stage>(Stage.Gamma);
  const [asanaProjectUrl, setAsanaProjectUrl] = useState("");
  const [actionError, setActionError] = useState<string>();
  const [projectSearch, setProjectSearch] = useState("");
  const [activeSync, setActiveSync] = useState<ActiveSync>();
  const [, setResolvedSyncIds] = useState<Set<string>>(() => new Set());

  const visibleProjects = useMemo(() => {
    const normalizedSearch = projectSearch.trim().toLocaleLowerCase();
    return projects
      .filter((project) =>
        project.projectName.toLocaleLowerCase().includes(normalizedSearch)
      )
      .sort((left, right) => {
        const byUpdatedAt =
          Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
        return byUpdatedAt || left.projectName.localeCompare(right.projectName);
      });
  }, [projectSearch, projects]);

  useEffect(() => {
    if (!activeSync || !isRunning(activeSync.sync)) {
      return undefined;
    }
    const { projectId, sync } = activeSync;
    const timer = window.setTimeout(() => {
      getSync(projectId, sync.syncId)
        .then((nextSync) => {
          if (isRunning(nextSync)) {
            setActiveSync({ projectId, sync: nextSync });
            return;
          }
          setResolvedSyncIds((current) =>
            new Set(current).add(nextSync.syncId)
          );
          setActiveSync(undefined);
          refresh().catch((refreshFailure: unknown) =>
            setActionError(errorMessage(refreshFailure))
          );
        })
        .catch((pollFailure: unknown) => {
          if (isAsanaAuthorizationError(pollFailure)) {
            markUnauthorized();
          }
          setActionError(errorMessage(pollFailure));
          setActiveSync(undefined);
        });
    }, sync.retryAfterMs ?? 1000);
    return () => window.clearTimeout(timer);
  }, [activeSync, getSync, markUnauthorized, refresh]);

  const closeCreateModal = () => {
    if (!isCreating) {
      setCreateVisible(false);
      setActionError(undefined);
    }
  };

  const create = () => {
    setActionError(undefined);
    const asanaProjectGid = asanaProjectGidFromUrl(asanaProjectUrl);
    if (!asanaProjectGid) {
      setActionError(
        "Enter an Asana project URL containing its numeric project ID."
      );
      return;
    }
    createProject({
      projectName,
      description: description || undefined,
      stage,
      asanaProjectGid,
      asanaProjectUrl,
    })
      .then(() => {
        setProjectName("");
        setDescription("");
        setStage(Stage.Gamma);
        setAsanaProjectUrl("");
        setCreateVisible(false);
      })
      .catch((createFailure: unknown) =>
        setActionError(errorMessage(createFailure))
      );
  };

  const connectAsana = () => {
    setActionError(undefined);
    beginAuthorization(needsReconnect).catch((authorizationFailure: unknown) =>
      setActionError(errorMessage(authorizationFailure))
    );
  };

  const sync = (project: UatProject) => {
    setActionError(undefined);
    startSync(project.id)
      .then((syncResult) => {
        if (isRunning(syncResult)) {
          setActiveSync({ projectId: project.id, sync: syncResult });
        } else {
          setResolvedSyncIds((current) =>
            new Set(current).add(syncResult.syncId)
          );
          refresh().catch((refreshFailure: unknown) =>
            setActionError(errorMessage(refreshFailure))
          );
        }
      })
      .catch((syncFailure: unknown) => {
        if (isAsanaAuthorizationError(syncFailure)) {
          markUnauthorized();
        }
        setActionError(errorMessage(syncFailure));
      });
  };

  let projectContent: React.ReactNode;
  if (error) {
    projectContent = <Alert type="error">{errorMessage(error)}</Alert>;
  } else if (isLoading) {
    projectContent = (
      <Box textAlign="center" padding="l">
        <StatusIndicator type="loading">Loading UAT Projects</StatusIndicator>
      </Box>
    );
  } else {
    projectContent = (
      <ProjectListContent
        projects={visibleProjects}
        projectOrders={projectOrders}
        activeSync={activeSync}
        onSync={sync}
      />
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {!isAuthorized && (
                  <Button loading={isConnecting} onClick={connectAsana}>
                    {needsReconnect ? "Reconnect Asana" : "Connect Asana"}
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => setCreateVisible(true)}
                >
                  Create UAT Project
                </Button>
              </SpaceBetween>
            }
          >
            Raven UAT Projects
          </Header>
        }
      >
        <FormField label="Search projects">
          <Input
            value={projectSearch}
            onChange={({ detail }) => setProjectSearch(detail.value)}
            placeholder="Search by project name"
          />
        </FormField>
      </Container>
      {actionError && <Alert type="error">{actionError}</Alert>}
      {authorizationError && <Alert type="error">{authorizationError}</Alert>}
      {createError && <Alert type="error">{errorMessage(createError)}</Alert>}
      {syncError && <Alert type="error">{errorMessage(syncError)}</Alert>}
      {projectContent}
      <Modal
        visible={createVisible}
        onDismiss={closeCreateModal}
        header="Create shared UAT Project"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={closeCreateModal} disabled={isCreating}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={isCreating}
                disabled={!projectName.trim() || !asanaProjectUrl.trim()}
                onClick={create}
              >
                Create project
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Project name">
            <Input
              value={projectName}
              onChange={({ detail }) => setProjectName(detail.value)}
              placeholder="TVOD EU5 expansion"
            />
          </FormField>
          <FormField label="Description">
            <Input
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              placeholder="What this UAT validates"
            />
          </FormField>
          <FormField label="Target stage">
            <Select
              selectedOption={{ label: stage, value: stage }}
              options={Object.values(Stage).map((value) => ({
                label: value,
                value,
              }))}
              onChange={({ detail }) =>
                setStage(detail.selectedOption.value as Stage)
              }
            />
          </FormField>
          <FormField
            label="Asana project URL"
            description="Paste the project URL. Lusca Search derives and stores its numeric Asana ID."
          >
            <Input
              value={asanaProjectUrl}
              onChange={({ detail }) => setAsanaProjectUrl(detail.value)}
              placeholder="https://app.asana.com/0/1200123456789012/list"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
